import React, { useCallback, useEffect, useMemo, useRef, useState, TouchEvent as ReactTouchEvent } from "react";
import { useHistory } from "react-router-dom";
import { Virtuoso } from "react-virtuoso";
import { Routes } from "router/config";
import { useTranslation } from "locales";
import useChatFolders from "hooks/useChatFolders";
import useGlobalMessageSearch from "hooks/useGlobalMessageSearch";
import { getBcp47Locale } from "lib/dateLocale";
import { stripMetaPrefix } from "lib/messageMetadata";
import providers from "lib/providers";
import BaseAvatar from "components/BaseAvatar";
import BaseSpinner from "components/BaseSpinner";
import CustomScrollBar from "components/CustomScrollBar";
import InfiniteScroll from "components/InfiniteScroll";
import useController from "./controller";
import ChatFolderTabs from "../ChatFolderTabs";
import MessageThreadItem from "../MessageThreadItem";

import "./style.scss";

interface Props {
  className?: string;
  onNewChat?: () => void;
  onCreateGroup?: () => void;
  onCreateChannel?: () => void;
}

function MessageThreadList({ className, onNewChat, onCreateGroup, onCreateChannel }: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const [searchQuery, setSearchQuery] = useState("");
  const [globalSearchActive, setGlobalSearchActive] = useState(false);
  const {
    messageThreadList,
    user,
    scrollThreads,
    loadMore,
    loadingThreads,
    refresh,
    hiddenThreads,
    showHidden,
    loadingHidden,
    toggleShowHidden,
    restoreThread,
    loadHiddenThreads,
  } = useController();

  const {
    folders,
    activeFolderId,
    setActiveFolderId,
    createFolder,
    renameFolder,
    deleteFolder,
    toggleThreadInFolder,
    getThreadFolderIds,
  } = useChatFolders(user?.id);

  const { globalResults, globalLoading, globalSearch, clearGlobalSearch } = useGlobalMessageSearch();
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contactSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [contactResults, setContactResults] = useState<any[]>([]);
  const [publicGroupResults, setPublicGroupResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Pull-to-refresh
  const [pullOffset, setPullOffset] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pullStartY = useRef<number | null>(null);
  const pullThreshold = 60;

  const handlePullStart = useCallback((e: ReactTouchEvent) => {
    pullStartY.current = e.touches[0].clientY;
  }, []);

  const handlePullMove = useCallback(
    (e: ReactTouchEvent) => {
      if (pullStartY.current === null || isRefreshing) return;
      const scrollEl = e.currentTarget.querySelector(".messages-thread-list__scrollbar > div");
      if (scrollEl && scrollEl.scrollTop > 0) return;
      const dy = e.touches[0].clientY - pullStartY.current;
      if (dy > 0) {
        setPullOffset(Math.min(dy * 0.5, 80));
      }
    },
    [isRefreshing]
  );

  const handlePullEnd = useCallback(() => {
    if (pullOffset >= pullThreshold && !isRefreshing) {
      setIsRefreshing(true);
      refresh().finally(() => {
        setIsRefreshing(false);
        setPullOffset(0);
      });
    } else {
      setPullOffset(0);
    }
    pullStartY.current = null;
  }, [pullOffset, isRefreshing, refresh]);

  // Cleanup search timers on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (contactSearchTimerRef.current) clearTimeout(contactSearchTimerRef.current);
    };
  }, []);

  const filteredThreads = useMemo(() => {
    let threads = messageThreadList;

    // Filter out self-threads (Saved Messages thread) — shown separately at the top
    threads = threads.filter((t: any) => {
      if (t.subject?.subjectType === "group") return true;
      const otherUsers = (t.users || []).filter((u: any) => String(u.id) !== String(user?.id));
      return otherUsers.length > 0;
    });

    // Folder filter
    if (activeFolderId) {
      const folder = folders.find((f) => f.id === activeFolderId);
      if (folder) {
        const folderThreadIds = new Set(folder.threadIds.map(String));
        threads = threads.filter((t: any) => folderThreadIds.has(String(t.id)));
      }
    }

    // Search filter
    if (searchQuery.trim() && !globalSearchActive) {
      const q = searchQuery.toLowerCase();
      threads = threads.filter(
        (thread: any) =>
          thread.users?.some(
            (u: any) => u.screenName?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q)
          ) || thread.groupName?.toLowerCase().includes(q)
      );
    }

    return threads;
  }, [messageThreadList, searchQuery, globalSearchActive, activeFolderId, folders]);

  // Compute unread counts per folder
  const folderUnreadCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const folder of folders) {
      const threadIdSet = new Set(folder.threadIds.map(String));
      counts[folder.id] = messageThreadList
        .filter((t: any) => threadIdSet.has(String(t.id)))
        // Match the row badge (MessageThreadItem): a group's General row rolls up
        // its topics via aggregatedUnreadCount, so the folder total stays equal to
        // the sum of its visible rows. Falls back to own count for personal/old data.
        .reduce((sum: number, t: any) => sum + (t.aggregatedUnreadCount ?? t.unreadMessageCount ?? 0), 0);
    }
    return counts;
  }, [folders, messageThreadList]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (contactSearchTimerRef.current) clearTimeout(contactSearchTimerRef.current);
      if (globalSearchActive && value.trim()) {
        // Debounce global search to prevent firing on every keystroke
        searchTimerRef.current = setTimeout(() => {
          globalSearch(value, messageThreadList);
        }, 400);
      } else if (!globalSearchActive && value.trim().length >= 2) {
        contactSearchTimerRef.current = setTimeout(async () => {
          setSearchLoading(true);
          const existingThreadGroupIds = new Set(
            messageThreadList
              .filter((t: any) => t.subject?.subjectType === "group")
              .map((t: any) => String(t.subject.subjectId))
          );
          const threadUserIds = new Set(messageThreadList.flatMap((t: any) => (t.userIds || []).map(String)));
          const [contactsRes, groupsRes] = await Promise.allSettled([
            providers.UserPublicDataProvider.search({
              data: { relevantTerm: value, size: 20 },
            }),
            providers.GroupsProvider.search({
              data: { isPublic: true, system: false, name: value },
            }),
          ]);
          if (contactsRes.status === "fulfilled") {
            setContactResults(
              (contactsRes.value.result || []).filter(
                (u: any) => String(u.id) !== String(user?.id) && !threadUserIds.has(String(u.id))
              )
            );
          } else {
            setContactResults([]);
          }
          if (groupsRes.status === "fulfilled") {
            // Hide groups the user is already in — they're already in the main
            // list and re-opening them via the discovery row would be confusing.
            setPublicGroupResults(
              (groupsRes.value || []).filter((g: any) => !existingThreadGroupIds.has(String(g.id)))
            );
          } else {
            setPublicGroupResults([]);
          }
          setSearchLoading(false);
        }, 400);
      } else {
        clearGlobalSearch();
        setContactResults([]);
        setPublicGroupResults([]);
      }
    },
    [globalSearchActive, globalSearch, clearGlobalSearch, messageThreadList, user?.id]
  );

  const handlePublicGroupClick = useCallback(
    async (group: any) => {
      const slugOrId = group.slug || group.id;
      // joinPublicGroup is idempotent — re-joining returns the current group
      // state — so we always call it instead of pre-checking membership.
      try {
        await providers.GroupsProvider.joinPublicGroup(String(group.id));
      } catch {
        /* navigate anyway; backend will reject sends if not a member */
      }
      history.push(`${Routes.messages}/group/${slugOrId}`);
    },
    [history]
  );

  const toggleSearchMode = useCallback(() => {
    setGlobalSearchActive((prev) => {
      const next = !prev;
      if (next && searchQuery.trim()) {
        globalSearch(searchQuery, messageThreadList);
        setContactResults([]);
      } else {
        clearGlobalSearch();
      }
      return next;
    });
  }, [searchQuery, messageThreadList, globalSearch, clearGlobalSearch]);

  const handleResultClick = useCallback(
    (result: any) => {
      const threadId = result.threadId;
      // Carry the found message id into the chat — MessagesList/GroupMessagesList
      // read it from router state, page history until it's loaded and highlight it.
      const state = { highlightMessageId: String(result.message.id) };
      // Find the thread to determine if it's a group or personal
      const thread = messageThreadList.find((t: any) => String(t.id) === String(threadId));
      if (thread && (thread as any).subject?.subjectType === "group") {
        history.push(`${Routes.messages}/group/${(thread as any).subject.subjectId}`, state);
      } else if (thread) {
        // Personal chat — navigate to the other user. Prefer the slug so the
        // controller's numeric-id→slug redirect (which drops router state)
        // doesn't kick in.
        const otherUser = thread.users?.find((u: any) => String(u.id) !== String(user?.id));
        if (otherUser) {
          history.push(`${Routes.messages}/${(otherUser as any).slug || otherUser.id}`, state);
        } else if (user) {
          // Self-thread — Saved Messages
          history.push(`${Routes.messages}/${user.id}`, state);
        }
      }
    },
    [messageThreadList, history, user]
  );

  const cls = ["messages-thread-list"];
  if (className) cls.push(className);

  if (!user) return null;
  return (
    <div className={cls.join(" ")}>
      <div className="messages-thread-list__header">
        <button
          type="button"
          className="messages-thread-list__back-btn"
          onClick={() => history.push(Routes.home)}
          title={t("Back")}
        >
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <div className="messages-thread-list__search">
          <svg className="messages-thread-list__search-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
          <input
            type="text"
            placeholder={globalSearchActive ? t("Search messages...") : t("Search")}
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <button
            type="button"
            className={`messages-thread-list__global-search-btn${globalSearchActive ? " __active" : ""}`}
            onClick={toggleSearchMode}
            title={globalSearchActive ? t("Search in chat names") : t("Search in messages")}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
        {onCreateChannel && (
          <button
            className="messages-thread-list__new-chat-btn"
            onClick={onCreateChannel}
            type="button"
            title={t("Create channel")}
          >
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 11v2h4v-2h-4zm-2 6.61c.96.71 2.21 1.65 3.2 2.39.4-.53.8-1.07 1.2-1.6-.99-.74-2.24-1.68-3.2-2.4-.4.54-.8 1.08-1.2 1.61zM20.4 5.6c-.4-.53-.8-1.07-1.2-1.6-.99.74-2.24 1.68-3.2 2.4.4.53.8 1.07 1.2 1.6.96-.72 2.21-1.65 3.2-2.4zM4 9c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h1v4h2v-4h1l5 3V6L8 9H4zm11.5 3c0-1.33-.58-2.53-1.5-3.35v6.69c.92-.81 1.5-2.01 1.5-3.34z" />
            </svg>
          </button>
        )}
        {onCreateGroup && (
          <button
            className="messages-thread-list__new-chat-btn"
            onClick={onCreateGroup}
            type="button"
            title={t("Create group")}
          >
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
            </svg>
          </button>
        )}
        {onNewChat && (
          <button
            className="messages-thread-list__new-chat-btn"
            onClick={onNewChat}
            type="button"
            title={t("New chat")}
          >
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 000-1.42l-2.34-2.34a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
            </svg>
          </button>
        )}
      </div>

      <ChatFolderTabs
        folders={folders}
        activeFolderId={activeFolderId}
        onSelectFolder={setActiveFolderId}
        onCreateFolder={createFolder}
        onRenameFolder={renameFolder}
        onDeleteFolder={deleteFolder}
        unreadCounts={folderUnreadCounts}
      />

      {/* Global search results */}
      {globalSearchActive && searchQuery.trim() && (
        <div className="messages-thread-list__global-results">
          {globalLoading && <BaseSpinner className="mx-auto" />}
          {!globalLoading && globalResults.length === 0 && (
            <div className="messages-thread-list__empty">{t("No messages found")}</div>
          )}
          {!globalLoading && globalResults.length > 0 && (
            <CustomScrollBar className="messages-thread-list__scrollbar">
              <div className="messages-thread-list__container">
                {globalResults.map((result, idx) => {
                  const thread = messageThreadList.find((t: any) => String(t.id) === String(result.threadId));
                  // A personal thread with no other participant is the user's
                  // self-thread — label it "Saved Messages", not "Chat".
                  const isSelfThread =
                    thread &&
                    (thread as any).subject?.subjectType !== "group" &&
                    !(thread.users || []).some((u: any) => String(u.id) !== String(user?.id));
                  const chatName = isSelfThread
                    ? t("Saved Messages")
                    : (thread as any)?._groupName ||
                      (thread as any)?.groupName ||
                      thread?.users?.find((u: any) => String(u.id) !== String(user?.id))?.screenName ||
                      t("Chat");
                  return (
                    <div
                      key={`${result.message.id}-${idx}`}
                      className="messages-thread-list__search-result"
                      onClick={() => handleResultClick(result)}
                    >
                      <div className="messages-thread-list__search-result-chat">{chatName}</div>
                      <div className="messages-thread-list__search-result-text">
                        {stripMetaPrefix(result.message.text).substring(0, 120)}
                      </div>
                      <div className="messages-thread-list__search-result-time">
                        {result.message.time ? new Date(result.message.time).toLocaleDateString(getBcp47Locale()) : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CustomScrollBar>
          )}
        </div>
      )}

      {/* Normal thread list */}
      {(!globalSearchActive || !searchQuery.trim()) && (
        <div
          onTouchStart={handlePullStart}
          onTouchMove={handlePullMove}
          onTouchEnd={handlePullEnd}
          style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          {/* Pull-to-refresh indicator */}
          {(pullOffset > 0 || isRefreshing) && (
            <div
              className="messages-thread-list__pull-indicator"
              style={{ height: isRefreshing ? 40 : pullOffset, transition: isRefreshing ? "height 0.3s" : "none" }}
            >
              {isRefreshing ? (
                <BaseSpinner />
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  style={{
                    opacity: Math.min(pullOffset / pullThreshold, 1),
                    transform: `rotate(${Math.min(pullOffset / pullThreshold, 1) * 180}deg)`,
                    transition: "transform 0.1s",
                  }}
                >
                  <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" fill="var(--msg-accent)" />
                </svg>
              )}
            </div>
          )}
          {!searchQuery && !activeFolderId && user && (
            <div
              className="messages-thread-list__saved-messages"
              onClick={() => history.push(`${Routes.messages}/${user.id}`)}
            >
              <div className="messages-thread-list__saved-icon">
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z" fill="currentColor" />
                </svg>
              </div>
              <div className="messages-thread-list__saved-info">
                <span className="messages-thread-list__saved-name">{t("Saved Messages")}</span>
              </div>
            </div>
          )}
          {filteredThreads.length > 0 && (
            <Virtuoso
              className="messages-thread-list__scrollbar"
              data={filteredThreads}
              defaultItemHeight={72}
              overscan={200}
              endReached={() => {
                if (scrollThreads && loadMore) loadMore(() => {});
              }}
              computeItemKey={(_index, item) => String(item.id)}
              itemContent={(_index, messageThread) => (
                <MessageThreadItem
                  authUser={user}
                  messageThread={messageThread}
                  onDeleted={refresh}
                  folders={folders}
                  threadFolderIds={getThreadFolderIds(String(messageThread.id))}
                  onToggleThreadInFolder={toggleThreadInFolder}
                  onCreateFolder={createFolder}
                />
              )}
            />
          )}
          {/* No list-load spinner: returning users hydrate the final list from cache
              instantly, and a cold start renders nothing until the list arrives. */}
          {/* Backend contact search results */}
          {!globalSearchActive && searchQuery.trim() && (
            <>
              {searchLoading && <BaseSpinner className="mx-auto" />}
              {!searchLoading && contactResults.length > 0 && (
                <div className="messages-thread-list__contact-results">
                  <div className="messages-thread-list__contact-results-header">{t("Contacts")}</div>
                  {contactResults.map((u: any) => (
                    <div
                      key={u.id}
                      className="messages-thread-list__contact-result"
                      onClick={() => history.push(`${Routes.messages}/${u.id}`)}
                    >
                      <BaseAvatar user={u} size={42} className="messages-thread-list__contact-avatar" />
                      <div className="messages-thread-list__contact-info">
                        <span className="messages-thread-list__contact-name">{u.screenName}</span>
                        {u.username && <span className="messages-thread-list__contact-username">@{u.username}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!searchLoading && publicGroupResults.length > 0 && (
                <div className="messages-thread-list__contact-results">
                  <div className="messages-thread-list__contact-results-header">{t("Public groups and channels")}</div>
                  {publicGroupResults.map((g: any) => (
                    <div
                      key={g.id}
                      className="messages-thread-list__contact-result"
                      onClick={() => handlePublicGroupClick(g)}
                    >
                      <div className="message-thread-item__group-avatar messages-thread-list__contact-avatar">
                        {g.avatarUrl ? (
                          <img src={g.avatarUrl} alt={g.name || ""} className="message-thread-item__group-avatar-img" />
                        ) : g.name ? (
                          g.name.charAt(0).toUpperCase()
                        ) : g.isChannel ? (
                          "C"
                        ) : (
                          "G"
                        )}
                      </div>
                      <div className="messages-thread-list__contact-info">
                        <span className="messages-thread-list__contact-name">
                          {g.name || (g.isChannel ? t("Channel") : t("Group"))}
                        </span>
                        <span className="messages-thread-list__contact-username">
                          {g.isChannel ? t("Channel") : t("Group")}
                          {typeof g.memberIds?.length === "number" ? ` · ${g.memberIds.length}` : ""}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {!loadingThreads &&
            filteredThreads.length === 0 &&
            contactResults.length === 0 &&
            publicGroupResults.length === 0 &&
            !searchLoading &&
            !showHidden && (
              <div className="messages-thread-list__empty">
                {searchQuery
                  ? t("No chats found")
                  : activeFolderId
                  ? t("No chats in this folder")
                  : t("No conversations yet")}
              </div>
            )}
          {showHidden && hiddenThreads.length > 0 && (
            <div className="messages-thread-list__hidden-section">
              {hiddenThreads.map((ht) => (
                <div key={ht.id} className="messages-thread-list__hidden-item-wrapper">
                  <MessageThreadItem
                    authUser={user}
                    messageThread={ht}
                    onDeleted={() => {
                      loadHiddenThreads();
                      refresh();
                    }}
                  />
                  <button
                    type="button"
                    className="messages-thread-list__hidden-restore-btn"
                    onClick={() => restoreThread(String(ht.id))}
                    title={t("Restore")}
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M13 3a9 9 0 00-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0013 21a9 9 0 000-18z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          {showHidden && loadingHidden && (
            <div style={{ padding: "8px 0", textAlign: "center" }}>
              <BaseSpinner className="mx-auto" />
            </div>
          )}
          {showHidden && !loadingHidden && hiddenThreads.length === 0 && (
            <div className="messages-thread-list__hidden-empty">{t("No archived chats")}</div>
          )}
          <div className="messages-thread-list__hidden-toggle">
            <button type="button" onClick={toggleShowHidden} className="messages-thread-list__hidden-btn">
              <svg viewBox="0 0 24 24" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M20 6H4l8 8z"
                  fill="currentColor"
                  style={{ transform: showHidden ? "rotate(180deg)" : "none", transformOrigin: "center" }}
                />
              </svg>
              {t("Archived chats")} {hiddenThreads.length > 0 && `(${hiddenThreads.length})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MessageThreadList;
